/**
 * Shared Game Picker Component
 * Shows today's (and optionally yesterday's) games as a schedule view.
 * Extracted from BoxScoreModal for reuse in Roster Intel and other views.
 *
 * Supports two modes:
 * 1. Single action (backward compat): `onSelectGame` — whole card clickable
 * 2. Dual action: `onSelectBoxScore` + `onSelectLineup` — two icon buttons per card
 */

import type { LeagueKey } from '@parlay-picker/shared/league';
import { getInSeasonLeagueKeys, getLeagueLabel } from '@parlay-picker/shared/league';
import { getTeamInfo } from '@parlay-picker/shared/teams';
import { ClipboardList, TableProperties } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { TeamLogo } from './TeamLogo';

// =============================================================================
// Types
// =============================================================================

export interface GamePickerGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
  commenceTime: string;
  leagueKey: LeagueKey;
  gameStatus: 'pre-game' | 'live' | 'final';
  homeScore: number | null;
  awayScore: number | null;
  quarter: number | null;
  timeRemaining: string | null;
}

export interface GamePickerProps {
  /** Initial league filter */
  leagueKey?: LeagueKey;
  /** Single-action mode: whole card clickable (BoxScoreModal backward compat) */
  onSelectGame?: (game: GamePickerGame) => void;
  /** Dual-action mode: box score button */
  onSelectBoxScore?: (game: GamePickerGame) => void;
  /** Dual-action mode: lineup/roster intel button */
  onSelectLineup?: (game: GamePickerGame) => void;
  /** Fetch URL base (for mobile, defaults to '') */
  apiBase?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function parseGameStatus(status: string): 'pre-game' | 'live' | 'final' {
  if (!status) return 'pre-game';
  const lower = status.toLowerCase();
  if (lower === 'final' || lower.includes('final')) return 'final';
  if (
    lower.includes('qtr') ||
    lower.includes('quarter') ||
    lower.includes('half') ||
    lower.includes('ot') ||
    lower === 'in progress'
  )
    return 'live';
  // ISO date string = pre-game with commence time
  if (/^\d{4}-\d{2}-\d{2}/.test(status)) return 'pre-game';
  return 'pre-game';
}

function CountdownTimer({ gameStartsAt }: { gameStartsAt: string }): ReactElement {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const startMs = new Date(gameStartsAt).getTime();
  const diff = startMs - now;

  if (diff <= 0) {
    return <span className="text-theme-warning text-sm font-medium">Starting soon</span>;
  }

  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1000);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);

  return <span className="text-sm font-mono text-theme-text-secondary">{parts.join(' ')}</span>;
}

// =============================================================================
// Game Card
// =============================================================================

function GameCard({
  game,
  onSelect,
  onBoxScore,
  onLineup,
}: {
  game: GamePickerGame;
  onSelect?: (game: GamePickerGame) => void;
  onBoxScore?: (game: GamePickerGame) => void;
  onLineup?: (game: GamePickerGame) => void;
}): ReactElement {
  const gameLeague = game.leagueKey;
  const homeInfo = getTeamInfo(game.homeTeam, gameLeague);
  const awayInfo = getTeamInfo(game.awayTeam, gameLeague);
  const isLive = game.gameStatus === 'live';
  const isFinal = game.gameStatus === 'final';
  const startMs = game.commenceTime ? new Date(game.commenceTime).getTime() : 0;
  const validTime = game.commenceTime && !Number.isNaN(startMs);
  const time = validTime
    ? new Date(game.commenceTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : '';

  // Dual action takes priority — when both box score + lineup callbacks are provided, show buttons
  const isDualAction = !!(onBoxScore || onLineup);

  const cardContent = (
    <>
      {/* Pre-game: time + countdown */}
      {!isLive && !isFinal && validTime && (
        <div className="mb-2 flex items-center justify-center gap-2 text-xs">
          <span className="font-mono text-white">{time}</span>
          <span className="text-theme-text-tertiary">·</span>
          <CountdownTimer gameStartsAt={game.commenceTime} />
        </div>
      )}

      {/* Teams + scores */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TeamLogo name={game.awayTeam} leagueKey={gameLeague} size="md" />
          <div>
            <span className="text-sm font-medium text-white">
              {awayInfo?.abbr ?? game.awayTeam}
            </span>
            {game.awayScore != null && (
              <span className="ml-2 text-sm font-bold font-mono text-white">{game.awayScore}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center">
          {isLive ? (
            <>
              <span className="text-[10px] px-1.5 py-0.5 rounded text-white font-bold bg-theme-danger">
                <span className="inline-block w-1.5 h-1.5 bg-white rounded-full mr-1 animate-pulse" />
                LIVE
              </span>
              {game.quarter && (
                <span className="text-[10px] text-theme-text-tertiary mt-0.5 font-mono">
                  {game.quarter <= 4 ? `Q${game.quarter}` : `OT${game.quarter - 4}`}
                  {game.timeRemaining ? ` ${game.timeRemaining}` : ''}
                </span>
              )}
            </>
          ) : isFinal ? (
            <span className="text-[10px] bg-theme-surface-hover px-1.5 py-0.5 rounded text-white font-medium">
              FINAL
            </span>
          ) : (
            <span className="text-xs text-theme-text-tertiary">@</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            {game.homeScore != null && (
              <span className="mr-2 text-sm font-bold font-mono text-white">{game.homeScore}</span>
            )}
            <span className="text-sm font-medium text-white">
              {homeInfo?.abbr ?? game.homeTeam}
            </span>
          </div>
          <TeamLogo name={game.homeTeam} leagueKey={gameLeague} size="md" />
        </div>
      </div>

      {/* Dual action buttons */}
      {isDualAction && (
        <div className="flex justify-center gap-2 mt-2 pt-2 border-t border-theme-border/20">
          {onBoxScore && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onBoxScore({ ...game, leagueKey: gameLeague });
              }}
              className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium text-theme-text-secondary hover:text-white hover:bg-theme-surface-hover transition"
              title="Box Score"
            >
              <TableProperties className="w-3.5 h-3.5" />
              Box Score
            </button>
          )}
          {onLineup && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onLineup({ ...game, leagueKey: gameLeague });
              }}
              className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium text-theme-text-secondary hover:text-white hover:bg-theme-surface-hover transition"
              title="Lineup"
            >
              <ClipboardList className="w-3.5 h-3.5" />
              Lineup
            </button>
          )}
        </div>
      )}
    </>
  );

  // In dual-action mode, card is not clickable — use buttons inside
  const isClickable = onSelect && !isDualAction;
  const cardClass = `rounded-lg border p-3 text-left transition-colors ${
    isFinal
      ? 'border-theme-border/60 bg-theme-surface-hover/20'
      : isLive
        ? 'border-theme-danger/50 bg-theme-surface/50'
        : 'border-theme-border/60 bg-theme-surface/50'
  } ${isClickable ? 'hover:bg-theme-surface-hover/50 hover:border-theme-border cursor-pointer' : ''}`;

  if (isClickable) {
    return (
      <button
        type="button"
        key={game.id}
        onClick={() => onSelect({ ...game, leagueKey: gameLeague })}
        className={cardClass}
      >
        {cardContent}
      </button>
    );
  }

  return (
    <div key={game.id} className={cardClass}>
      {cardContent}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function GamePicker({
  leagueKey: initialLeague,
  onSelectGame,
  onSelectBoxScore,
  onSelectLineup,
  apiBase = '',
}: GamePickerProps): ReactElement {
  const inSeasonLeagues = useMemo(() => getInSeasonLeagueKeys(), []);
  const [selectedLeague, setSelectedLeague] = useState<'all' | LeagueKey>(
    initialLeague && inSeasonLeagues.includes(initialLeague)
      ? initialLeague
      : inSeasonLeagues.length === 1
        ? inSeasonLeagues[0]
        : 'all',
  );
  const [games, setGames] = useState<GamePickerGame[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchGames = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/live-stats/games`);
      if (!res.ok) return;
      const data = await res.json();
      const list: GamePickerGame[] = (data.games || []).map(
        (g: {
          id: string;
          homeTeam: string;
          awayTeam: string;
          status: string;
          startTime: string | null;
          leagueKey: LeagueKey;
          homeScore: number | null;
          awayScore: number | null;
          quarter: number | null;
          timeRemaining: string | null;
        }) => ({
          id: g.id,
          homeTeam: g.homeTeam,
          awayTeam: g.awayTeam,
          commenceTime: g.startTime || '',
          leagueKey: g.leagueKey,
          gameStatus: parseGameStatus(g.status),
          homeScore: g.homeScore,
          awayScore: g.awayScore,
          quarter: g.quarter,
          timeRemaining: g.timeRemaining,
        }),
      );
      // Sort: live first, then pre-game by start time, then final
      list.sort((a, b) => {
        const order = { live: 0, 'pre-game': 1, final: 2 };
        const aOrder = order[a.gameStatus] ?? 1;
        const bOrder = order[b.gameStatus] ?? 1;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return new Date(a.commenceTime).getTime() - new Date(b.commenceTime).getTime();
      });
      setGames(list);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // Filter by league
  const filteredGames = useMemo(
    () => (selectedLeague === 'all' ? games : games.filter((g) => g.leagueKey === selectedLeague)),
    [games, selectedLeague],
  );

  // League tabs
  const leagueTabs =
    inSeasonLeagues.length > 1 ? (
      <div className="flex gap-1.5 pb-3 overflow-x-auto">
        <button
          type="button"
          onClick={() => setSelectedLeague('all')}
          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
            selectedLeague === 'all'
              ? 'bg-theme-primary-fill text-theme-on-primary-fill'
              : 'bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text-secondary'
          }`}
        >
          All
        </button>
        {inSeasonLeagues.map((lk) => (
          <button
            type="button"
            key={lk}
            onClick={() => setSelectedLeague(lk)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
              selectedLeague === lk
                ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                : 'bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text-secondary'
            }`}
          >
            {getLeagueLabel(lk)}
          </button>
        ))}
      </div>
    ) : null;

  // Loading skeleton
  if (loading) {
    return (
      <div>
        {leagueTabs}
        <div className="py-6 space-y-3 animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="flex items-center justify-between px-3 py-3 rounded-lg bg-theme-surface-hover/30"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-theme-surface-hover" />
                <div className="w-20 h-3 rounded bg-theme-surface-hover" />
              </div>
              <div className="w-12 h-3 rounded bg-theme-surface-hover" />
              <div className="flex items-center gap-3">
                <div className="w-20 h-3 rounded bg-theme-surface-hover" />
                <div className="w-8 h-8 rounded-full bg-theme-surface-hover" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (filteredGames.length === 0) {
    return (
      <div>
        {leagueTabs}
        <div className="text-center py-12">
          <div className="text-theme-text-tertiary text-sm">
            No games scheduled for this league today
          </div>
        </div>
      </div>
    );
  }

  // Group games by league
  const grouped = new Map<LeagueKey, GamePickerGame[]>();
  for (const game of filteredGames) {
    const lk = game.leagueKey;
    if (!grouped.has(lk)) grouped.set(lk, []);
    grouped.get(lk)!.push(game);
  }

  return (
    <div>
      {leagueTabs}
      {Array.from(grouped.entries()).map(([lk, leagueGames]) => (
        <div key={lk} className="mb-4 last:mb-0">
          {selectedLeague === 'all' && grouped.size > 1 && (
            <div className="text-[10px] font-medium text-theme-text-tertiary uppercase tracking-wider mb-2">
              {getLeagueLabel(lk)}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {leagueGames.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                onSelect={onSelectGame}
                onBoxScore={onSelectBoxScore}
                onLineup={onSelectLineup}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
